import {
  PROJECT_DEVICES_FETCH_STARTED,
  PROJECT_DEVICES_FETCH_SUCCEEDED,
  PROJECT_DEVICES_FETCH_FAILED,
  PROJECT_DEVICES_CHANGE_PAGE,
  PROJECT_DEVICES_CHANGE_ORDER_AND_ORDER_BY,
  PROJECT_DEVICES_SET_FILTER,
  PROJECT_DEVICES_SET_DEVICE_TYPE,
  PROJECT_DEVICES_SET_FORM_DEVICES
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { getDeviceDiff } from '#component/device/devices-popup/helper';
import { snackBarError } from '#action/fetch';
import { getRequestOptions, getSearchParamsWithoutEmptyFields } from '#helper';

let abortController = new AbortController();

export const fetchApplianceGroupDevices = (projectId, exclude, isCreateForm, data = {}) =>
  async (dispatch, getState) => {
    try {
      const {
        page,
        itemsPerPage,
        order,
        orderBy,
        filter,
        filterBy,
        deviceType,
        addMode
      } = getState().projectDevicesPopup;
      const { groupData, applianceId, alertId } = data || {};
      const initialFormValues = groupData?.devices || [];

      const formDevices = data?.formDevices ||
        groupData?.devices ||
        data?.applianceGroup?.devices ||
        getState().projectDevicesPopup?.formDevices ||
        [];
      const addedToFormDevices = formDevices;

      abortController.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      dispatch({
        type: PROJECT_DEVICES_FETCH_STARTED
      });

      const limit = itemsPerPage;
      const offset = (page - 1) * itemsPerPage;
      const searchParams = new URLSearchParams({
        order,
        orderBy,
        offset,
        limit,
        pagination: 1,
        occupierDetails: 1,
        withoutYoungest: 1
      });

      const openedFromCreateForm = isCreateForm && !applianceId;

      // Create new appliance.
      // Opened from create form in edit mode.
      if (openedFromCreateForm) {
        searchParams.delete('projects');
        const devicesIds = formDevices?.map(d => d.id).join(',');

        if (!addMode) {
          if (!formDevices.length) {
            dispatch({
              type: PROJECT_DEVICES_FETCH_SUCCEEDED,
              payload: {
                devices: []
              }
            });
            return;
          }
          searchParams.set('ids', devicesIds);
        } else {
          const addedDevicesIds = addedToFormDevices?.map(d => d.id).join(',');
          searchParams.set('excludeIds', addedDevicesIds);
        }
      }

      // Opened from edit form
      if (isCreateForm && applianceId) {
        if (!addMode && formDevices.length === 0) { // No devices in edit mode
          dispatch({
            type: PROJECT_DEVICES_FETCH_SUCCEEDED,
            payload: {
              devices: [],
              alertId
            }
          });
          return;
        }
        const ids = formDevices.map(d => d.id).join(',');
        searchParams.set('ids', ids);
        // If form devices input was changed
        if (formDevices?.length !== initialFormValues?.length && !addMode) {
          // New devices was added
          if (formDevices?.length > initialFormValues?.length) {
            const devicesIds = formDevices.map(d => d.id).join(',');
            searchParams.set('ids', devicesIds);
          } else { // Devices was removed
            const devicesDiff = getDeviceDiff(initialFormValues, formDevices);
            const excludedDevicesIds = devicesDiff.map(d => d.id).join(',');
            const devicesIds = formDevices.map(d => d.id).join(',');
            searchParams.set('ids', devicesIds);
            searchParams.set('excludeIds', excludedDevicesIds);
          }
        }
      }

      if (deviceType) {
        if (deviceType.value === 'All types') {
          searchParams.delete('types');
        } else {
          searchParams.set('types', deviceType.value);
        }
      }

      if (filter && typeof filter === 'string') {
        searchParams.set('filterBy', filterBy);
        searchParams.set('filter', filter);
      }

      if (exclude || addMode) {
        searchParams.delete('projects');
        searchParams.delete('ids');
        const devicesIds = formDevices.map(d => d.id).join(',');
        searchParams.set('excludeIds', devicesIds);
      }

      // Debounce to do not send multiple requests in short time
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, 200);
      });

      const paramsWithoutEmptyFields = getSearchParamsWithoutEmptyFields(searchParams);
      const url = webPortalApi.buildApiUrl(`device/distinct/details?${paramsWithoutEmptyFields}`);
      const res = await fetch(url, { ...getRequestOptions(), signal });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError('Response is not json');
      }
      if (!res.ok) {
        dispatch(
          snackBarError({
            message: 'Error in request'
          })
        );
      }

      const { devices, last } = await res.json();

      const actionPayload = {
        type: PROJECT_DEVICES_FETCH_SUCCEEDED,
        payload: {
          devices,
          alertId,
          formData: data,
          formDevices,
          exclude
        },
        totalCount: last
      };

      dispatch(actionPayload);
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      dispatch({
        type: PROJECT_DEVICES_FETCH_FAILED,
        error: e
      });
      console.error(e);
    }
  };

export const setFormData = formDevices => dispatch => {
  try {
    const payload = {
      formDevices
    };

    dispatch({
      type: PROJECT_DEVICES_SET_FORM_DEVICES,
      payload,
      totalCount: formDevices?.length
    });
  } catch (e) {
    console.warn(e);
  }
};

export const changeOrderAndOrderBy = (
  newOrder,
  newOrderBy,
  projectId,
  addMode,
  isCreateForm,
  applianceData
) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_CHANGE_ORDER_AND_ORDER_BY,
    payload: { order: newOrder, orderBy: newOrderBy }
  });
  dispatch(fetchApplianceGroupDevices(projectId, addMode, isCreateForm, applianceData));
};

export const changePage = (
  newPage,
  projectId,
  addMode,
  isCreateForm,
  applianceData,
  refetch = true
) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchApplianceGroupDevices(projectId, addMode, isCreateForm, applianceData));
  }
};

export const setFilter = (newFilter, projectId, addMode, isCreateForm, applianceData) =>
  dispatch => {
    dispatch({
      type: PROJECT_DEVICES_SET_FILTER,
      payload: newFilter
    });

    dispatch(fetchApplianceGroupDevices(projectId, addMode, isCreateForm, applianceData));
  };

export const setDeviceType = (newType, projectId, addMode, openedFromCreateForm, applianceData) =>
  dispatch => {
    dispatch({
      type: PROJECT_DEVICES_SET_DEVICE_TYPE,
      payload: newType
    });

    dispatch({
      type: PROJECT_DEVICES_CHANGE_PAGE,
      payload: 1
    });

    dispatch(fetchApplianceGroupDevices(projectId, addMode, openedFromCreateForm, applianceData));
  };
